import React, { useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import clsx from 'clsx'
import { useMutation } from '@apollo/client'
import { SHARE_FORM } from '../../../../graphql/mutation'
import { toast } from 'react-toastify'

interface IEmailModalProps {
  closeModal: () => void
  formId: any
  botgoUrl: string
}

const initialValues = {
  recipientEmails: ''
}

const shareViaEmailSchema = Yup.object().shape({
  recipientEmails: Yup.string()
    .required('At least one email is required')
    .test('is-valid-emails', 'Please enter valid email addresses', (value) => {
      if (!value) return false

      const emails = value.split(',').map((email) => email.trim())

      // Safer regex with limited repetition and no backtracking vulnerability
      const safeEmailRegex =
        /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/

      return emails.every((email) => {
        // Basic length check to prevent excessive processing
        if (email.length > 254) return false

        return safeEmailRegex.test(email)
      })
    })
})

const ShareViaEmailModel: React.FC<IEmailModalProps> = ({ closeModal, formId, botgoUrl }) => {
  const [loadingOL, setLoading] = useState(false)
  const [shareForm] = useMutation(SHARE_FORM, {
    context: {
      headers: {
        'access-control-allow-methods': 'Form-Builder'
      }
    }
  })

  const formik = useFormik({
    initialValues,
    validationSchema: shareViaEmailSchema,
    onSubmit: (values) => {
      const recipientEmailsArray = values.recipientEmails.split(',').map((email) => email.trim())

      setLoading(true)
      shareForm({
        variables: {
          input: {
            formUuid: formId,
            shareableLink: botgoUrl,
            recipientEmails: recipientEmailsArray
          }
        }
      })
        .then((res: any) => {
          setLoading(false)
          closeModal()
          if (res.data.shareForm.status >= 200 && res.data.shareForm.status < 300) {
            toast.success(res.data.shareForm.message)
          } else {
            toast.warning(res.data.shareForm.message)
          }
        })
        .catch((err) => {
          setLoading(false)
          toast.error('Sorry! Unexpected Error.')
          console.error(err)
        })
    }
  })

  return (
    <div className='card w-500px h-460px mb-4 position-relative text-center'>
      <button
        type='button'
        className='btn-close position-absolute top-0 end-0 m-n4 rounded-circle'
        aria-label='Close'
        onClick={closeModal}
        style={{
          backgroundColor: 'rgba(218, 222, 243)',
          height: '25px',
          width: '25px',
          color: 'rgba(52, 60, 106)'
        }}
      ></button>
      <div className='d-flex justify-content-between align-items-center px-3 py-2'>
        <h2 className='mb-0'>Share via Email</h2>
      </div>
      <div className='d-flex justify-content-between align-items-center px-3 py-2'>
        <p className='mb-0'>Enter recipient's email address to share the form.</p>
      </div>

      {/* Form Start */}
      <form onSubmit={formik.handleSubmit}>
        <div className='d-flex flex-column mb-10 fv-row fv-plugins-icon-container'>
          <div className='d-flex align-items-start mt-6 px-3 py-2'>
            <label className='fs-6 fw-bold mb-2'>Email(s):</label>
          </div>
          <input
            placeholder='Enter email addresses separated by commas'
            type='text'
            autoComplete='off'
            {...formik.getFieldProps('recipientEmails')}
            className={clsx(
              'form-control form-control-lg form-control-solid',
              { 'is-invalid': formik.touched.recipientEmails && formik.errors.recipientEmails },
              { 'is-valid': formik.touched.recipientEmails && !formik.errors.recipientEmails }
            )}
          />
          {formik.touched.recipientEmails && formik.errors.recipientEmails && (
            <div className='d-flex align-items-start mt-1 n2 px-3 py-2'>
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.recipientEmails}</span>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className='text-start'>
          <button
            type='submit'
            id='kt_sign_up_submit'
            className='btn btn-lg btn-primary w-100 mb-5'
            disabled={formik.isSubmitting || !formik.isValid || loadingOL}
          >
            {!loadingOL && <span className='indicator-label'>Send</span>}
            {loadingOL && (
              <span className='indicator-progress' style={{ display: 'block' }}>
                Please wait...{' '}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
      </form>
      {/* Form End */}
    </div>
  )
}

export default ShareViaEmailModel
